import React, { useEffect, useState } from 'react';
import { Field } from 'react-final-form';
import Select from 'react-select';
import classNames from 'classnames';

import css from './FieldSingleSelect.module.css';

const customStyles = {
  control: (styles, state) => ({
    ...styles,
    backgroundColor: '#fff',
    border: state.isFocused ? '1px solid var(--marketplaceColor)' : '1px solid #d8dce6',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(26, 115, 232, 0.2)' : 'none',
    '&:hover': { borderColor: state.isFocused ? 'var(--marketplaceColor)' : '#b2b8c5' },
    minHeight: '50px',
    paddingTop: '5px',
    paddingBottom: '5px',
    paddingLeft: '16px',
    paddingRight: '12px',
    margin: '0px',
    position: 'relative',
    fontFamily: "'Inter', sans-serif",
  }),

  dropdownIndicator: (base, state) => ({
    ...base,
    paddingRight: 0,
    transition: 'all .2s ease',
    fontFamily: "'Inter', sans-serif",
    transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : null,
  }),
  indicatorSeparator: styles => ({ display: 'none' }),
  valueContainer: (base, state) => ({
    ...base,
    padding: 0,
    margin: 0,
  }),
  input: (base, state) => ({
    ...base,
    margin: 0,
    padding: '2px 4px 2px 6px',
    minHeight: 'auto',
    color: '#000',
  }),
  inputContainer: (base, state) => ({
    ...base,
    margin: 0,
    padding: 0,
  }),
  option: (styles, { isFocused, isDisabled, isSelected }) => ({
    ...styles,
    color: '#000',
    padding: '12px 16px',
    fontSize: '16px',
    fontWeight: '400',
    lineHeight: '100%',
    letterSpacing: '-0.04em',
    fontFamily: "'Inter', sans-serif",
    backgroundColor: isDisabled ? null : isFocused ? 'var(--colorGrey100)' : isSelected ? '#FFF' : null,
  }),
  singleValue: (styles, { isFocused, isDisabled, isSelected }) => ({
    ...styles,
    color: '#4a4a4a',
    lineHeight: '118%',
    fontFamily: "'Inter', sans-serif;",
    fontSize: '16px',
    fontWeight: '400',
    marginLeft: 0,
    marginRight: 0,
  }),
  menuPortal: base => ({ ...base, postion: 'absolute', top: '0', left: '0' }),
  menu: base => ({
    ...base,
    borderRadius: '4px',
    marginTop: '8px',
    backgroundColor: '#fff',
    color: '#000',
    fontFamily: "'Inter', sans-serif",
  }),
  menuList: base => ({
    ...base,
    padding: 0,
    backgroundColor: '#fff',
    fontSize: '16px',
    fontWeight: '400',
    fontFamily: "'Inter', sans-serif",
    lineHeight: '100%',
    letterSpacing: '-0.04em',
    color: '#000',
  }),
  placeholder: base => ({
    ...base,
    padding: '6px 10px',
    backgroundColor: '#fff',
    fontSize: '16px',
    fontWeight: '400',
    lineHeight: '100%',
    letterSpacing: '-0.04em',
    color: '#b2b2b2',
    fontFamily: "'Inter', sans-serif;",
  }),
  noOptionsMessage: base => ({
    ...base,
    padding: '12px 16px',
  }),
};

/**
 * A searchable react-select picker in a final-form field. react-select only renders in the
 * browser, so the server's page and the browser's first render show the label and an empty
 * box, and the picker mounts after.
 */
const FieldSingleSelect = props => {
  const {
    id,
    name,
    options,
    label,
    className,
    hideLabel = false,
    placeholderText,
    validate,
    isClearable = false,
  } = props;

  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => {
    setIsMounted(true);
  }, []);

  const inputId = id || name;

  return options ? (
    <Field name={name} validate={validate}>
      {fieldProps => {
        const { input, meta } = fieldProps;
        const { onChange, value, ...rest } = input;

        // Convert stored value string back to full object for react-select display
        const displayValue = value
          ? options.find(option => option.value === value) || null
          : null;

        const hasError = meta.touched && meta.error;
        const status = hasError ? css.error : null;

        return (
          <div className={className}>
            {label ? (
              <label
                htmlFor={inputId}
                className={classNames({
                  [css.hideLabel]: hideLabel,
                })}
              >
                {label}
              </label>
            ) : null}
            <div className={status}>
              {isMounted ? (
                <Select
                  inputId={inputId}
                  className={css.selectContainer}
                  styles={customStyles}
                  classNamePrefix="react-select"
                  closeMenuOnSelect={true}
                  placeholder={placeholderText}
                  isMulti={false}
                  isSearchable={true}
                  isClearable={isClearable}
                  tabSelectsValue={false}
                  components={{
                    IndicatorSeparator: () => null,
                  }}
                  options={options}
                  value={displayValue}
                  getOptionValue={option => option.value}
                  {...rest}
                  onChange={item => {
                    // Extract only the value (ID) from the selected item
                    const selectedValue = item ? item.value : null;
                    input.onChange(selectedValue);
                  }}
                />
              ) : (
                <div className={css.placeholderBox} />
              )}
            </div>
            {hasError ? <span className={css.errorText}>{meta.error}</span> : null}
          </div>
        );
      }}
    </Field>
  ) : null;
};

export default FieldSingleSelect;
